import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Send, ChevronDown, Clock, Loader2, Check, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { addDays, nextMonday, format, isWeekend } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ScheduleSendDialog } from './ScheduleSendDialog';

type SendState = 'idle' | 'sending' | 'sent' | 'error';

interface ScheduleSendButtonProps {
  onSend: () => void;
  onSchedule: (date: Date) => void;
  sendState: SendState;
  disabled?: boolean;
}

function getNextBusinessDay(from: Date): Date {
  let d = addDays(from, 1);
  while (isWeekend(d)) {
    d = addDays(d, 1);
  }
  return d;
}

function getQuickOptions() {
  const now = new Date();
  const tomorrow = getNextBusinessDay(now);
  const monday = nextMonday(now);

  const opt08 = new Date(tomorrow);
  opt08.setHours(8, 0, 0, 0);

  const opt14 = new Date(tomorrow);
  opt14.setHours(14, 0, 0, 0);

  const optMon = new Date(monday);
  optMon.setHours(8, 0, 0, 0);

  const fmtDay = (d: Date) => format(d, "EEEE, dd/MM", { locale: ptBR });

  const options = [
    { label: 'Amanhã, 08:00', hint: fmtDay(opt08), date: opt08 },
    { label: 'Amanhã, 14:00', hint: fmtDay(opt14), date: opt14 },
  ];

  // Only add Monday option if Monday is different from tomorrow
  if (optMon.getTime() !== opt08.getTime()) {
    options.push({ label: 'Próxima segunda, 08:00', hint: fmtDay(optMon), date: optMon });
  }

  return options;
}

export function ScheduleSendButton({
  onSend,
  onSchedule,
  sendState,
  disabled,
}: ScheduleSendButtonProps) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const options = getQuickOptions();

  const sendButtonContent = () => {
    switch (sendState) {
      case 'sending':
        return <><Loader2 className="h-4 w-4 animate-spin" /> Enviando...</>;
      case 'sent':
        return <><Check className="h-4 w-4 text-green-400" /> Enviado!</>;
      case 'error':
        return <><AlertCircle className="h-4 w-4" /> Falha ao enviar</>;
      default:
        return <><Send className="h-4 w-4" /> Enviar</>;
    }
  };

  const handleQuickSchedule = (date: Date) => {
    setPopoverOpen(false);
    onSchedule(date);
  };

  return (
    <>
      <div className="flex">
        <Button
          onClick={onSend}
          disabled={disabled || sendState === 'sending' || sendState === 'sent'}
          className={cn(
            'gap-2 px-4 py-2.5 rounded-r-none',
            sendState === 'sent' && 'bg-green-600 hover:bg-green-600',
            sendState === 'error' && 'bg-red-600 hover:bg-red-600 animate-shake'
          )}
        >
          {sendButtonContent()}
        </Button>
        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              disabled={disabled || sendState === 'sending' || sendState === 'sent'}
              className="rounded-l-none border-l border-white/30 hover:bg-white/10 px-2"
            >
              <ChevronDown className="h-3.5 w-3.5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-64 p-1">
            <div className="p-2 border-b text-xs font-semibold flex items-center gap-1.5 text-foreground">
              <Clock className="h-3.5 w-3.5 text-primary" />
              Agendar envio
            </div>
            {options.map((opt) => (
              <button
                key={opt.label}
                onClick={() => handleQuickSchedule(opt.date)}
                className="w-full flex items-center justify-between px-2 py-1.5 text-sm rounded hover:bg-muted transition-colors"
              >
                <span className="text-foreground">{opt.label}</span>
                <span className="text-xs text-muted-foreground">{opt.hint}</span>
              </button>
            ))}
            <div className="border-t p-1">
              <button
                onClick={() => { setPopoverOpen(false); setDialogOpen(true); }}
                className="w-full text-xs text-primary hover:bg-primary/5 rounded px-2 py-1.5 text-left transition-colors"
              >
                Data e hora personalizada...
              </button>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <ScheduleSendDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSchedule={onSchedule}
      />
    </>
  );
}
